import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, Plus, Edit, Trash2, Users, DollarSign, MapPin, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Holidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [holidayForm, setHolidayForm] = useState({
    name: '',
    date: '',
    is_recurring: false,
    is_closed: true,
    skeleton_crew_needed: 0,
    skeleton_crew_roles: [],
    region: 'default',
    holiday_pay_multiplier: 1.5,
    notes: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isManagerOrAdmin = ['admin', 'manager'].includes(user.role);

  useEffect(() => {
    fetchHolidays();
    if (isManagerOrAdmin) {
      fetchVolunteers();
    }
  }, [selectedYear]);

  const fetchHolidays = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/company-holidays?year=${selectedYear}`, { headers });
      setHolidays(response.data || []);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
    }
  };

  const fetchVolunteers = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/holiday-volunteers`, { headers });
      setVolunteers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch volunteers:', error);
    }
  };

  const handleSaveHoliday = async () => {
    if (!holidayForm.name || !holidayForm.date) {
      toast.error('Please fill in holiday name and date');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingHoliday) {
        await axios.put(`${API}/company-holidays/${editingHoliday.id}`, holidayForm, { headers });
        toast.success('Holiday updated');
      } else {
        await axios.post(`${API}/company-holidays`, holidayForm, { headers });
        toast.success('Holiday created');
      }

      // After saving, update the selected year to match the holiday's year
      const holidayYear = parseInt(holidayForm.date.split('-')[0]);
      if (holidayYear !== selectedYear) {
        setSelectedYear(holidayYear);
      }

      setShowHolidayModal(false);
      resetForm();
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save holiday');
    } finally {
      setLoading(false);
    }
  };

  const handleEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    
    // Extract the actual year from the holiday date (not the selected year filter)
    const actualDate = holiday.date;
    
    setHolidayForm({
      name: holiday.name,
      date: actualDate,  // Use actual date, not filtered date
      is_recurring: holiday.is_recurring,
      is_closed: holiday.is_closed,
      skeleton_crew_needed: holiday.skeleton_crew_needed || 0,
      skeleton_crew_roles: holiday.skeleton_crew_roles || [],
      region: holiday.region || 'default',
      holiday_pay_multiplier: holiday.holiday_pay_multiplier || 1.5,
      notes: holiday.notes || ''
    });
    setShowHolidayModal(true);
  };

  const handleDeleteHoliday = async (holidayId) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/company-holidays/${holidayId}`, { headers });
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  const handleVolunteer = async (holidayId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/holiday-volunteers?holiday_id=${holidayId}`, {}, { headers });
      toast.success('Volunteer request submitted');
      fetchVolunteers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to volunteer');
    }
  };

  const handleAcceptVolunteer = async (volunteerId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/holiday-volunteers/${volunteerId}/accept`, {}, { headers });
      toast.success('Volunteer accepted');
      fetchVolunteers();
    } catch (error) {
      toast.error('Failed to accept volunteer');
    }
  };

  const resetForm = () => {
    setEditingHoliday(null);
    setHolidayForm({
      name: '',
      date: '',
      is_recurring: false,
      is_closed: true,
      skeleton_crew_needed: 0,
      skeleton_crew_roles: [],
      region: 'default',
      holiday_pay_multiplier: 1.5,
      notes: ''
    });
  };

  const formatDate = (dateStr) => {
    // Parse date string as local date to avoid timezone shift
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Company Holidays</h2>
          <p className="text-gray-600 mt-1">Manage company holidays and holiday coverage</p>
        </div>
        <div className="flex items-center space-x-4">
          <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isManagerOrAdmin && (
            <Button onClick={() => setShowHolidayModal(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Holiday
            </Button>
          )}
        </div>
      </div>

      {/* Holidays Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {holidays.map((holiday) => {
          const hasVolunteered = volunteers.some(v => v.holiday_id === holiday.id && v.user_id === user.id);
          const holidayVolunteers = volunteers.filter(v => v.holiday_id === holiday.id);
          
          return (
            <Card key={holiday.id} className={`${holiday.is_closed ? 'border-red-200' : 'border-yellow-200'}`}>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{holiday.name}</h3>
                      <div className="flex items-center space-x-2 text-sm text-gray-600 mt-1">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(holiday.date)}</span>
                        {holiday.is_recurring && (
                          <Badge variant="outline" className="text-xs">Recurring</Badge>
                        )}
                      </div>
                    </div>
                    {isManagerOrAdmin && (
                      <div className="flex space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditHoliday(holiday)}
                          className="h-6 w-6 p-0"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteHoliday(holiday.id)}
                          className="h-6 w-6 p-0 text-red-600"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 text-xs">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-3 w-3" />
                      <span className={holiday.is_closed ? 'text-red-600 font-medium' : 'text-yellow-600 font-medium'}>
                        {holiday.is_closed ? 'Company Closed' : `Skeleton Crew: ${holiday.skeleton_crew_needed}`}
                      </span>
                    </div>
                    
                    {holiday.holiday_pay_multiplier && holiday.holiday_pay_multiplier > 1 && (
                      <div className="flex items-center space-x-2 text-green-600">
                        <DollarSign className="h-3 w-3" />
                        <span>{holiday.holiday_pay_multiplier}x pay for working</span>
                      </div>
                    )}

                    {holiday.region && holiday.region !== 'default' && (
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-3 w-3" />
                        <span>Region: {holiday.region}</span>
                      </div>
                    )}
                  </div>

                  {!holiday.is_closed && (
                    <div className="pt-2 border-t">
                      {!hasVolunteered ? (
                        <Button
                          size="sm"
                          onClick={() => handleVolunteer(holiday.id)}
                          className="w-full bg-green-600 hover:bg-green-700 text-xs"
                        >
                          <Users className="h-3 w-3 mr-1" />
                          Volunteer to Work
                        </Button>
                      ) : (
                        <Badge className="w-full justify-center bg-green-100 text-green-800">
                          You volunteered
                        </Badge>
                      )}

                      {isManagerOrAdmin && holidayVolunteers.length > 0 && (
                        <div className="mt-2 text-xs text-gray-600">
                          {holidayVolunteers.length} volunteer{holidayVolunteers.length > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Volunteers Section - Managers Only */}
      {isManagerOrAdmin && volunteers.filter(v => v.status === 'pending').length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Holiday Volunteers</CardTitle>
            <CardDescription>Review and accept volunteers for holiday coverage</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {volunteers.filter(v => v.status === 'pending').map((volunteer) => (
                <div key={volunteer.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <p className="font-medium">{volunteer.user?.name}</p>
                    <p className="text-sm text-gray-600">{volunteer.holiday?.name} - {formatDate(volunteer.holiday?.date)}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAcceptVolunteer(volunteer.id)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Accept
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Holiday Modal */}
      <Dialog open={showHolidayModal} onOpenChange={setShowHolidayModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingHoliday ? 'Edit Holiday' : 'Add Company Holiday'}</DialogTitle>
            <DialogDescription>
              Configure holiday details and coverage requirements
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Holiday Name *</Label>
              <Input
                id="name"
                value={holidayForm.name}
                onChange={(e) => setHolidayForm({...holidayForm, name: e.target.value})}
                placeholder="e.g., Christmas Day"
              />
            </div>

            <div>
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type={holidayForm.is_recurring ? 'text' : 'date'}
                value={holidayForm.date}
                onChange={(e) => setHolidayForm({...holidayForm, date: e.target.value})}
                placeholder={holidayForm.is_recurring ? 'MM-DD' : 'YYYY-MM-DD'}
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="recurring"
                checked={holidayForm.is_recurring}
                onChange={(e) => setHolidayForm({...holidayForm, is_recurring: e.target.checked})}
                className="w-4 h-4"
              />
              <Label htmlFor="recurring">Recurring annually</Label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="closed"
                checked={holidayForm.is_closed}
                onChange={(e) => setHolidayForm({...holidayForm, is_closed: e.target.checked})}
                className="w-4 h-4"
              />
              <Label htmlFor="closed">Company fully closed</Label>
            </div>

            {!holidayForm.is_closed && (
              <div>
                <Label htmlFor="skeleton">Skeleton Crew Needed</Label>
                <Input
                  id="skeleton"
                  type="number"
                  min="0"
                  value={holidayForm.skeleton_crew_needed}
                  onChange={(e) => setHolidayForm({...holidayForm, skeleton_crew_needed: parseInt(e.target.value)})}
                />
              </div>
            )}

            <div>
              <Label htmlFor="multiplier">Holiday Pay Multiplier</Label>
              <Input
                id="multiplier"
                type="number"
                step="0.1"
                min="1"
                value={holidayForm.holiday_pay_multiplier}
                onChange={(e) => setHolidayForm({...holidayForm, holiday_pay_multiplier: parseFloat(e.target.value)})}
              />
              <p className="text-xs text-gray-500 mt-1">e.g., 1.5 = time and a half</p>
            </div>

            <div>
              <Label htmlFor="region">Region</Label>
              <Input
                id="region"
                value={holidayForm.region}
                onChange={(e) => setHolidayForm({...holidayForm, region: e.target.value})}
                placeholder="default"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={holidayForm.notes}
                onChange={(e) => setHolidayForm({...holidayForm, notes: e.target.value})}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowHolidayModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSaveHoliday} disabled={loading}>
              {editingHoliday ? 'Update' : 'Create'} Holiday
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Holidays;
